import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Plus, Filter, LayoutGrid, List, Grid3X3, X, Search } from 'lucide-react';
import SpiralSpinner from '../SpiralSpinner';
import '../DeckVisualizer.css';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Toggle } from '../ui/toggle';
import SectionHeaderShadcn from './SectionHeader.shadcn';
import { cn } from '../../lib/utils';
import { ALL } from '../../hooks/useCardFilters';

/**
 * One faceted filter dropdown.
 *
 * Options come from the filter hook's narrowing, so this renders whatever is
 * actually reachable given the other selections. A dimension with nothing to
 * choose between (zero or one option) is disabled rather than hidden — hiding
 * it would make the filter row jump around as selections change.
 */
function FilterSelect({ label, value, options, onChange, allLabel, disabled, title }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-9" title={title} aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

FilterSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  allLabel: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  title: PropTypes.string,
};

/** Map a list of primitives into `{value, label}` option objects. */
const toOptions = (values, formatLabel = (v) => String(v)) =>
  values.map((v) => ({ value: String(v), label: formatLabel(v) }));

/**
 * Card search + filter sidebar module.
 *
 * Filtering runs entirely client-side over the loaded card catalog (see
 * useCardCatalog / useCardFilters), so results update as you type and there is
 * no Search button — the previous server-round-trip-per-query flow required a
 * query before it would show anything at all.
 */
function CardSearchShadcn({
  isCollapsed,
  onToggle,
  filters,
  visibleResults,
  hasMoreResults,
  onResultsScroll,
  catalogLoading,
  catalogError,
  onAddCard,
  onHoverCard,
  getProxyImageUrl,
  collapseFilters,
  onToggleFilters,
  viewMode,
  onViewModeChange,
  t = () => null,
  hideHeader = false,
}) {
  const scrollAreaRef = useRef(null);

  const {
    searchQuery,
    setSearchQuery,
    cardTypeFilter,
    setCardTypeFilter,
    battleTypeFilter,
    setBattleTypeFilter,
    characterFilter,
    setCharacterFilter,
    levelFilter,
    setLevelFilter,
    roundFilter,
    setRoundFilter,
    rarityFilter,
    setRarityFilter,
    sceneMode,
    availableCardTypes,
    availableBattleTypes,
    availableCharacters,
    availableLevels,
    availableRounds,
    availableRarities,
    filteredCards,
    activeFilterCount,
    clearFilters,
  } = filters;

  // After results render, if everything fits without overflow, reveal more.
  // Large monitors can show a whole page of results without ever scrolling,
  // which would otherwise strand the rest of the matches behind a scroll that
  // never happens.
  useEffect(() => {
    if (!hasMoreResults) return;
    const el = scrollAreaRef.current;
    if (!el) return;
    const viewport = el.querySelector('[data-radix-scroll-area-viewport]') || el;
    if (viewport.scrollHeight <= viewport.clientHeight) {
      onResultsScroll();
    }
  }, [visibleResults, hasMoreResults, onResultsScroll]);

  const handleScroll = (e) => {
    // e.target is the Radix viewport, not the root wrapper (e.currentTarget).
    // Math.ceil(scrollTop) handles sub-pixel rounding at non-100% zoom.
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - Math.ceil(scrollTop) - clientHeight < 100) {
      onResultsScroll();
    }
  };

  const isGridView = viewMode !== 'details';
  const hasActiveFilters = activeFilterCount > 0;
  const totalMatches = filteredCards.length;

  return (
    <div className="flex flex-col gap-2 max-w-full overflow-hidden h-full min-h-0">
      {!hideHeader && (
        <SectionHeaderShadcn
          title={t('sidebar.search.title') || 'Card Search'}
          isCollapsed={isCollapsed}
          onToggle={onToggle}
          t={t}
        />
      )}

      {!isCollapsed && (
        <div className="flex flex-col flex-1 min-h-0 gap-2">
          {/* Filter Panel */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-3 flex-shrink-0">
            {/* Search Input — filters as you type, no submit */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder={
                  t('sidebar.search.placeholder') || 'Search name, number, character or effect…'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-10"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setSearchQuery('')}
                  aria-label={t('common.clear') || 'Clear search'}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Actions Row */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={collapseFilters ? 'outline' : 'secondary'}
                size="sm"
                onClick={onToggleFilters}
                className="gap-1"
              >
                <Filter className="h-4 w-4" />
                {t('sidebar.search.filters') || 'Filters'}
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>

              {/* View Mode Toggles */}
              <div className="flex items-center border rounded-md">
                <Toggle
                  pressed={viewMode === 'details'}
                  onPressedChange={() => onViewModeChange('details')}
                  size="sm"
                  className="rounded-r-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  title={t('sidebar.search.listView') || 'List view'}
                >
                  <List className="h-4 w-4" />
                </Toggle>
                <Toggle
                  pressed={viewMode === 'medium'}
                  onPressedChange={() => onViewModeChange('medium')}
                  size="sm"
                  className="rounded-none border-x data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  title={t('sidebar.search.mediumGrid') || 'Medium grid'}
                >
                  <Grid3X3 className="h-4 w-4" />
                </Toggle>
                <Toggle
                  pressed={viewMode === 'large'}
                  onPressedChange={() => onViewModeChange('large')}
                  size="sm"
                  className="rounded-l-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                  title={t('sidebar.search.largeGrid') || 'Large grid'}
                >
                  <LayoutGrid className="h-4 w-4" />
                </Toggle>
              </div>

              {/* Live match count — replaces the old Search button. */}
              <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                {catalogLoading
                  ? t('common.loading') || 'Loading…'
                  : t('sidebar.search.matchCount', { count: totalMatches }) ||
                    `${totalMatches} card${totalMatches === 1 ? '' : 's'}`}
              </span>
            </div>

            {/* Expandable Filters */}
            {!collapseFilters && (
              <div className="space-y-2 pt-2 border-t">
                <div className="grid grid-cols-2 gap-2">
                  <FilterSelect
                    label={t('sidebar.search.cardType') || 'Card Type'}
                    allLabel={t('sidebar.search.allCardTypes') || 'All Card Types'}
                    value={cardTypeFilter}
                    options={toOptions(availableCardTypes)}
                    onChange={setCardTypeFilter}
                  />

                  {/* Scenes have a field-round requirement instead of a level,
                      so this slot swaps between the two rather than showing a
                      Level dropdown that can only ever read "all". */}
                  {sceneMode ? (
                    <FilterSelect
                      label={t('sidebar.search.round') || 'Round'}
                      allLabel={t('sidebar.search.allRounds') || 'All Rounds'}
                      value={roundFilter}
                      options={toOptions(availableRounds, (r) => `Round ${r}`)}
                      onChange={setRoundFilter}
                    />
                  ) : (
                    <FilterSelect
                      label={t('sidebar.search.level') || 'Level'}
                      allLabel={t('sidebar.search.allLevels') || 'All Levels'}
                      value={levelFilter}
                      options={toOptions(availableLevels, (l) => `Level ${l}`)}
                      onChange={setLevelFilter}
                    />
                  )}

                  <FilterSelect
                    label={t('sidebar.search.type') || 'Type'}
                    allLabel={t('sidebar.search.allTypes') || 'All Types'}
                    value={battleTypeFilter}
                    options={toOptions(availableBattleTypes)}
                    onChange={setBattleTypeFilter}
                    disabled={sceneMode}
                    title={
                      sceneMode
                        ? t('sidebar.search.sceneNoType') || 'Scene cards have no battle type'
                        : undefined
                    }
                  />

                  <FilterSelect
                    label={t('sidebar.search.character') || 'Character'}
                    allLabel={t('sidebar.search.allCharacters') || 'All Characters'}
                    value={characterFilter}
                    options={toOptions(availableCharacters)}
                    onChange={setCharacterFilter}
                  />

                  <FilterSelect
                    label={t('sidebar.search.rarity') || 'Rarity'}
                    allLabel={t('sidebar.search.allRarities') || 'All Rarities'}
                    value={rarityFilter}
                    options={toOptions(availableRarities)}
                    onChange={setRarityFilter}
                  />
                </div>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="w-full text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t('sidebar.search.clearFilters') || 'Clear Filters'}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Catalog load failure — filtering can't work without the pool. */}
          {catalogError && (
            <Alert variant="destructive">
              <AlertDescription>{catalogError}</AlertDescription>
            </Alert>
          )}

          {/* Results */}
          {visibleResults.length > 0 && (
            <ScrollArea
              ref={scrollAreaRef}
              className="flex-1 min-h-[200px] rounded-lg border bg-background"
              onScrollCapture={handleScroll}
            >
              <div
                className={cn(
                  'p-2',
                  viewMode === 'details'
                    ? 'flex flex-col gap-2'
                    : viewMode === 'large'
                    ? 'grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2'
                    : 'grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-2'
                )}
              >
                {visibleResults.map((card) => {
                  const isScene = card.cardTypeLabel === 'Scene' || card.feature?.value === 'scene';

                  return (
                    <div
                      key={card.number || card.id}
                      onClick={() => onAddCard(card)}
                      onMouseEnter={() => onHoverCard && onHoverCard(card)}
                      className={cn(
                        'flex cursor-pointer rounded-md bg-card transition-colors hover:bg-accent',
                        isGridView
                          ? 'flex-col items-center text-center p-2'
                          : 'items-center gap-3 p-2'
                      )}
                    >
                      <img
                        src={getProxyImageUrl(
                          card.images?.blob?.thumbnail || card.images?.original?.thumbnail
                        )}
                        alt={card.name}
                        className={cn(
                          'object-cover rounded',
                          isGridView
                            ? isScene
                              ? 'w-full aspect-[88/63]'
                              : 'w-full aspect-[63/88]'
                            : 'w-12 h-[67px]'
                        )}
                        loading="lazy"
                      />
                      {!isGridView && (
                        <>
                          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <span className="font-semibold truncate text-sm">
                              {card.name}
                              {card.typeName ? ` ${card.typeName}` : ''}
                            </span>
                            <span className="text-xs text-muted-foreground">{card.number}</span>
                            <div className="flex gap-1 flex-wrap">
                              {card.cardTypeLabel && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  {card.cardTypeLabel}
                                </Badge>
                              )}
                              {card.levelValue != null && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  Lv.{card.levelValue}
                                </Badge>
                              )}
                              {isScene && card.roundValue != null && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  R{card.roundValue}
                                </Badge>
                              )}
                              {card.characterLabel && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0">
                                  {card.characterLabel}
                                </Badge>
                              )}
                              {card.rarityLabel && (
                                <Badge variant="secondary" className="text-[10px] px-1 py-0">
                                  {card.rarityLabel}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title={t('common.add') || 'Add'}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddCard(card);
                            }}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}

          {/* Initial catalog load */}
          {catalogLoading && visibleResults.length === 0 && (
            <SpiralSpinner size="sm" className="py-8" />
          )}

          {/* No matches — distinct from "still loading" and from "load failed" */}
          {!catalogLoading && !catalogError && totalMatches === 0 && (
            <div className="text-center text-muted-foreground py-8 italic">
              {t('sidebar.search.noResults') || 'No cards match these filters'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

CardSearchShadcn.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  /** The full return value of useCardFilters. */
  filters: PropTypes.object.isRequired,
  /** The windowed slice of `filters.filteredCards` currently rendered. */
  visibleResults: PropTypes.array.isRequired,
  hasMoreResults: PropTypes.bool.isRequired,
  onResultsScroll: PropTypes.func.isRequired,
  catalogLoading: PropTypes.bool,
  catalogError: PropTypes.string,
  onAddCard: PropTypes.func.isRequired,
  onHoverCard: PropTypes.func,
  getProxyImageUrl: PropTypes.func.isRequired,
  collapseFilters: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  viewMode: PropTypes.string,
  onViewModeChange: PropTypes.func,
  t: PropTypes.func,
  hideHeader: PropTypes.bool,
};

export default CardSearchShadcn;
